/**
 * @prettier
 */

import React, { useCallback } from 'react';
import _values from 'lodash/values';
import _fromPairs from 'lodash/fromPairs';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { connect } from 'react-redux';
import { getMultiSampleState } from 'cards/selectors/singlesample';
import { setMultiSampleEnabled as setMultiSampleEnabledAction } from 'cards/actions/singlesample';
import { openModal, hideModal } from 'cards/actions/modalActions';
import FeatureFlags from '../../../../../util/featureFlags';
import { useModalCloseCallback } from '../util/hooks';
import styles from './styles.module.less';

export const CustomizeButtonBase = ({
  cardType,
  multiSampleState,
  setMultiSampleEnabled,
  showModal,
  closeModal,
}) => {
  const preventClose = FeatureFlags.get('invitesSinglesampleSaveToDraftsOnExit');

  const closeCallbackBase = useModalCloseCallback();

  const handleOpenModal = useCallback(
    (overrideProps = {}) => {
      const closeCallback = (opts = {}) => {
        closeCallbackBase(opts);
        // * If the interstitial close state is displayed, and therefore the closemodal dismissed
        // * No need to update the sample state and fetch results.
        if (!(opts.modalOptions && opts.modalOptions.preventClose)) {
          setMultiSampleEnabled(cardType, false);
        }
      };

      const originalModalOptions = {
        warnOnClose: !preventClose,
        preventClose,
        closeCallback,
      };

      const cards = _values(multiSampleState.selected);
      const initialColorSet = _fromPairs(cards.map(card => [card.suiteUUID, card.initialColor]));

      return showModal(
        {
          entryPoint: 'PLP_MULTI',
          card: cards[0],
          isMulti: true,
          cardSuiteUUID: cards[0].suiteUUID,
          cardSuiteUUIDList: cards.map(card => card.suiteUUID),
          initialColor: initialColorSet[cards[0].suiteUUID],
          initialColorSet,
          handleOpenModal,
          initialCloseCallback: closeCallback,
          closeModal,
          originalModalOptions,
          ...overrideProps,
        },
        originalModalOptions
      );
    },
    [
      closeCallbackBase,
      closeModal,
      multiSampleState.selected,
      showModal,
      preventClose,
      cardType,
      setMultiSampleEnabled,
    ]
  );

  if (!multiSampleState.enabled) {
    return null;
  }

  const numSelected = Object.keys(multiSampleState.selected).length;

  const buttonDisabled = numSelected === 0;

  let buttonText;

  if (buttonDisabled) {
    buttonText = 'Select Designs To Customize';
  } else {
    buttonText = `Customize ${numSelected} Sample${numSelected === 1 ? '' : 's'}`;
  }

  return (
    <div className={styles.customizationButtonBox}>
      <ButtonV2 disabled={buttonDisabled} onClick={handleOpenModal} fullWidth>
        {buttonText}
      </ButtonV2>
    </div>
  );
};

export const mapStateToProps = state => ({
  multiSampleState: getMultiSampleState(state),
});

const mapDispatchToProps = dispatch => ({
  showModal: (props, overrideOptions = {}) =>
    dispatch(
      openModal('SINGLE_SAMPLE_MODAL', props, {
        warnOnClose: true,
        bottomModalOnMobile: true,
        className: styles.modal,
        ...overrideOptions,
      })
    ),
  closeModal: (opts = {}) => {
    const { shouldNotDisableMulti } = opts;
    if (shouldNotDisableMulti) {
      return dispatch(hideModal());
    }

    return dispatch(setMultiSampleEnabledAction(null, false)).then(() => dispatch(hideModal()));
  },
  setMultiSampleEnabled: (cardType, enabled) =>
    dispatch(setMultiSampleEnabledAction(cardType, enabled)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomizeButtonBase);
